import { clamp, mapRange } from "gsap/all";
import ScrollCall from "./HorizontalScrollCall";

export default class HorizontalView {
    constructor($el, $parent, parentIndex, opts = {}) {
        // Parameters
        this.$view = $el;
        this.opts = opts;

        this.$parent = $parent
        this.parentIndex = parentIndex

        // Get data attributes
        this.intersectionStart = 0;
        this.intersectionEnd = 0;
        this.progressStart = 0;
        this.progressEnd = 1;
        this.repeat = $el.dataset["hscrollRepeat"] != undefined ? true : false;
        this.translate = $el.dataset["hscrollTranslate"] != undefined ? parseInt($el.dataset["hscrollTranslate"], 10) : 0;
        this.rotation = $el.dataset["hscrollRotation"] != undefined ? parseInt($el.dataset["hscrollRotation"], 10) : 0;
        this.speed = $el.dataset["hscrollSpeed"] != undefined ? $el.dataset["hscrollSpeed"] : 1;
        this.call = $el.dataset["hscrollCall"] ? $el.dataset["hscrollCall"] : null;

        // States
        this.isInteractive = false;
        this.isInview = false;

        // Flags
        this.isIntersected = false;

        // Limits
        this.intersection = {
            start: 0,
            end: 0,
        };

        // Metrics
        this.metrics = {
            offsetLeft: 0,
            offsetRight: 0,
            bcr: {},
        };

        // Scroll Values
        this.pixelsScrolled = 0;

        // Class
        this.inViewClass = "is-inview";

        // Setup
        this.init();
    }

    ////////////////////
    // Lifecycle
    ////////////////////
    /**
     * Init
     */
    init() {
    }

    /**
     * Destroy
     */
    destroy() {
    }

    ////////////////////
    // Callbacks
    ////////////////////
    /**
     * Scroll callback
     */
    onScroll(pixelsScrolled = 0) {
        if (!this.isInteractive) return;
        this.pixelsScrolled = pixelsScrolled;
        this._computeProgress();
    }

    /**
     * Resize callback
     */
    onResize(pixelsScrolled = 0, scrollMetrics) {
        this.pixelsScrolled = pixelsScrolled;
        this._setIntersection(scrollMetrics[this.parentIndex]);
        this._computeProgress();
    }

    ////////////////////
    // Setters
    ////////////////////

    /**
     * Compute custom intersection values like root margin
     */
    _setIntersection(scrollMetrics) {
        const viewBCR = this.$view.getBoundingClientRect();
        const parentBCR = this.$parent.getBoundingClientRect();

        const translate = this.translate != null ? this.translate : 0

        this.intersection = {
            start: this.parentIndex > 0 ? scrollMetrics.start + viewBCR.left - parentBCR.left : scrollMetrics.start + viewBCR.left - parentBCR.left - scrollMetrics.rootWidth,
            end: this.parentIndex > 0 ? 
                scrollMetrics.start + viewBCR.left - parentBCR.left + viewBCR.width + translate + scrollMetrics.rootWidth
                : scrollMetrics.start + viewBCR.left - parentBCR.left + viewBCR.width + translate,
        };
    }

    /**
     * Set Interactive State
     * @param {boolean} : if is inview (IO based)
     */
    setInteractiveArea(isInteractive) {
        this.isInteractive = isInteractive;

        // First time to compute progress
        if (!this.isIntersected) {
            this.isIntersected = true;
            this._computeProgress();
        }
    }

    /**
     * Set Inview Class
     * @param {boolean} : if is inview (progress based)
     */
    _setInviewClass(isInview) {
        this.isInview = isInview;

        if (this.isInview) {
            this.$view.classList.add(this.inViewClass);
        } else {
            this.$view.classList.remove(this.inViewClass);
        }
    }

    ////////////////////
    // Progress
    ////////////////////
    /**
     * Compute progress
     */
    _computeProgress() {
        const defaultProgress = clamp(0, 1, mapRange(this.intersection.start, this.intersection.end, 0, 1, this.pixelsScrolled))
        const progress = defaultProgress;
        let currentProgress = clamp(this.progressStart, this.progressEnd, mapRange(0, 1, this.progressStart, this.progressEnd, progress))
        currentProgress = ((100000 * currentProgress) | 0) / 100000;
        this._animate(currentProgress)

        // Set inview class
        if (progress > 0 && progress < 1 && !this.isInview) {
            this._setInviewClass(true);
            this._dispatchScrollCall(true);
        } else if (progress === 1 && this.isInview && this.repeat) {
            this._setInviewClass(false);
            this._dispatchScrollCall(false);
        } else if (progress === 0 && this.isInview && this.repeat) {
            this._setInviewClass(false);
            this._dispatchScrollCall(false);
        }
    }

    _animate(progress) {
        if (this.translate || this.rotation) {
            let transform = ''

            if (this.translate) {
                const translate = mapRange(0, 1, this.translate * -1 * this.speed, this.translate * this.speed, progress)
                transform += `translate3d(${translate}%, 0, 0) `
            }

            if (this.rotation) {
                const rotation = mapRange(0, 1, this.rotation * -1 * this.speed, this.rotation * this.speed, progress)
                transform += `rotate(${rotation}deg) `
            }

            this.$view.style.transform = transform
        }
    }

    ////////////////////
    // Scroll Call
    ////////////////////
    /**
     * Dispatch scroll call
     * @param {boolean} : if is inview (progress based)
     * Launch data-hscroll-call function
     */
     _dispatchScrollCall(isInview) {
        if (isInview && this.call) {
            requestAnimationFrame(() => {
                ScrollCall.dispatch(this.call, "enter", this);
            })
        } else if (this.call) {
            ScrollCall.dispatch(this.call, "exit", this);
        }
    }
}
